import { zodResolver } from '@hookform/resolvers/zod'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { InceptionTypeInputEnum } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { CatAndSubCatDefaultCategoryFragment } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { useCreateProduct } from '@/modules/admin-center/hooks/useCreateProduct'
import { Button } from '@/modules/shared/components/button/Button'
import {
  CreateProductFormInputs,
  CreateProductFormSchemaWithType,
} from '@/modules/shared/components/create-product-modal/CreateProductFormSchema'
import CreateProductInformation from '@/modules/shared/components/create-product-modal/CreateProductInformation'
import CreateProductPreview from '@/modules/shared/components/create-product-modal/CreateProductPreview'
import CreateProductValues from '@/modules/shared/components/create-product-modal/CreateProductValues'
import { useCreateAndAddProductBuyList } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductBuyList'
import { useCreateAndAddProductCatalog } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductCatalog'
import { useCreateAndAddProductCreditNote } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductCreditNote'
import { useCreateAndAddProductGRNote } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductGRNote'
import { useCreateAndAddProductInvoice } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductInvoice'
import { useCreateAndAddProductRequisition } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductRequisition'
import { useGetCreateProductModal } from '@/modules/shared/components/create-product-modal/hooks/useGetCreateProductModal'
import CropImage from '@/modules/shared/components/crop-image/CropImage'
import { Modal } from '@/modules/shared/components/modal/ModalComponent'
import ProductSellPack from '@/modules/shared/components/product-sizes/ProductSellPack'
import ProductUnitSize from '@/modules/shared/components/product-sizes/ProductUnitSize'
import { displayToast } from '@/modules/shared/components/toast/displayToast'
import UploadFile from '@/modules/shared/components/upload-file/UploadFile'
import { supportedImageTypes } from '@/modules/shared/constants'
import { useAlert } from '@/modules/shared/hooks/useAlert'
import { UploadIcon } from '@/modules/shared/icons/UploadIcon'
import { ProductListType } from '@/modules/shared/types/ProductListType'
import convertUrlToFile from '@/modules/shared/utils/convertUrlToFile'
import { resizeImage } from '@/modules/shared/utils/resizeImage'

interface CreateProductModalProps {
  type: ProductListType
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  title: string
  id?: number
  refetch?: () => void
  quickAdd?: boolean
  isUtility?: boolean
  defaultProductId?: number
}

export default function CreateProductModal({
  type,
  showModal,
  setShowModal,
  title,
  id,
  refetch,
  quickAdd,
  isUtility,
  defaultProductId,
}: CreateProductModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { product, loadingStates, error } = useGetCreateProductModal({
    productId: defaultProductId || 0,
    skip: !showModal || !defaultProductId,
  })
  const [selectedCategory, setSelectedCategory] = useState<CatAndSubCatDefaultCategoryFragment | null>(null)
  const [removeDefaultCategory, setRemoveDefaultCategory] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageURL, setImageURL] = useState<string | undefined>()
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null)
  const [remount, setRemount] = useState(0)
  const { currentOrganisation, currentLegalEntity } = useSessionUser()
  const { alertDialog } = useAlert()

  const [createProduct, { loading: createProductLoading }] = useCreateProduct()

  const formMethods = useForm({
    defaultValues: {
      taxPercentage: 0,
      unitPrice: 0,
    },
    resolver: zodResolver(CreateProductFormSchemaWithType(type)),
  })

  const onCloseModal = () => {
    setSelectedFile(null)
    setImageURL(undefined)
    setCropImageUrl(null)
    setShowModal(false)
    setRemoveDefaultCategory(false)
    formMethods.reset()
  }

  const { onCreateAndAddProductBuyList, loading: createAndAddProductBuyListLoading } = useCreateAndAddProductBuyList({
    quickAdd,
    refetch,
    onCloseModal,
    id: String(id),
  })
  const { onCreateAndAddProductCatalog, loading: createAndAddProductCatalogLoading } = useCreateAndAddProductCatalog({
    isUtility,
    quickAdd,
    refetch,
    onCloseModal,
    id: String(id),
  })
  const { onCreateAndAddProductGRNote, loading: createAndAddProductGRNoteLoading } = useCreateAndAddProductGRNote({
    id: String(id),
  })
  const { onCreateAndAddProductInvoice, loading: createAndAddProductInvoiceLoading } = useCreateAndAddProductInvoice({
    quickAdd,
    refetch,
    onCloseModal,
    id: String(id),
  })
  const { onCreateAndAddProductCreditNote, loading: createAndAddProductCreditNoteLoading } =
    useCreateAndAddProductCreditNote({
      quickAdd,
      refetch,
      onCloseModal,
      id: String(id),
    })
  const { onCreateAndAddProductRequisition, loading: createAndAddProductRequisitionLoading } =
    useCreateAndAddProductRequisition({ quickAdd, refetch, onCloseModal, id: String(id) })

  const onSubmit: SubmitHandler<CreateProductFormInputs> = async (data: CreateProductFormInputs) => {
    let imageFile = null
    let resizedImage = null

    if (cropImageUrl || product?.image) {
      imageFile = await convertUrlToFile(cropImageUrl || product?.image || '')
      resizedImage = await resizeImage(imageFile, { width: 800, height: 800 })
    }

    const {
      categoryId,
      brand,
      itemDescription,
      itemMeasure,
      itemSize,
      itemPackName,
      itemSellPackName,
      itemSellQuantity,
      code,
      externalId,
    } = data
    if (type === ProductListType.AllProducts) {
      createProduct({
        variables: {
          input: {
            categoryId,
            brand,
            itemDescription,
            locale: 'en',
            itemMeasure,
            itemSize,
            itemPackName,
            itemSellPackName,
            itemSellQuantity,
            externalId,
            inceptionType: InceptionTypeInputEnum.Manual,
            ownerCode: code,
            image: resizedImage,
          },
        },
        onCompleted: (data) => {
          onCloseModal()
          displayToast({ title: t('general.productCreated', 'Product Successfully Created') })
          navigate(
            generatePath('/products/:productId/details', {
              productId: String(data.createProduct.product?.id),
            })
          )
        },
        onError() {
          alertDialog({ type: 'error' })
        },
      })
    } else if (type === ProductListType.AddCatalogProduct) {
      onCreateAndAddProductCatalog(data, !!currentOrganisation?.isAPurchaser, resizedImage)
    } else if (type === ProductListType.AddBuyListProduct) {
      onCreateAndAddProductBuyList(data, !!currentOrganisation?.isAPurchaser, resizedImage)
    } else if (type === ProductListType.AddInvoiceLine) {
      onCreateAndAddProductInvoice(data, !!currentOrganisation?.isAPurchaser, resizedImage)
    } else if (type === ProductListType.AddReceivingNoteProduct) {
      onCreateAndAddProductGRNote(data, !!currentOrganisation?.isAPurchaser, resizedImage)
    } else if (type === ProductListType.AddRequisitionProduct) {
      onCreateAndAddProductRequisition(data, !!currentOrganisation?.isAPurchaser, resizedImage)
    } else if (type === ProductListType.AddCreditNoteLine) {
      onCreateAndAddProductCreditNote(data, !!currentOrganisation?.isAPurchaser, resizedImage)
    }
  }

  const getSubmitButtonText = () => {
    switch (type) {
      case ProductListType.AddCatalogProduct:
        return t('general.addToCatalog', 'Add to Catalog')
      case ProductListType.AddBuyListProduct:
        return t('buyLists.builder.addToBuyList', 'Add to Buy List')
      case ProductListType.AddInvoiceLine:
        return t('general.addToInvoice', 'Add to Invoice')
      case ProductListType.AddReceivingNoteProduct:
        return t('general.addProduct', 'Add Product')
      case ProductListType.AddRequisitionProduct:
        return t('general.addToRequisition', 'Add to Requisition')
      case ProductListType.AllProducts:
        return t('general.createProduct', 'Create Product')
      case ProductListType.AddCreditNoteLine:
        return t('general.addToCreditNote', 'Add to Credit Note')
      default:
        return ''
    }
  }

  useEffect(() => {
    if (selectedFile) {
      setImageURL(URL.createObjectURL(selectedFile))
    } else {
      setImageURL(undefined)
      setCropImageUrl(null)
      setRemount(remount + 1)
    }

    return () => {
      if (imageURL) {
        URL.revokeObjectURL(imageURL)
      }
    }
  }, [selectedFile])

  useEffect(() => {
    if (product) {
      formMethods.reset({
        itemSize: product?.itemSize || undefined,
        itemMeasure: product?.itemMeasure || undefined,
        itemPackName: product?.itemPackName || undefined,
        itemSellPackName: product?.itemSellPackName || 'each',
        itemSellQuantity: product?.itemSellQuantity || 1,
        categoryId: product?.category?.id,
        brand: product?.brand || undefined,
        itemDescription: product?.itemDescription || undefined,
        code: product?.productCode || undefined,
      })
      setSelectedCategory(product?.category ? { ...product?.category } : null)
    }
  }, [product])

  const getDescription = () => {
    if (product) {
      return (
        <Trans
          t={t}
          i18nKey="createProductModal.cloneDescription"
          values={{ currentLegalEntity: currentLegalEntity?.name }}
        >
          Here you can clone and create a new product owned by your Organisation, from the product you have selected.
          The newly created product will appear in your My Products area, and {{ currentLegalEntity }} will be set as
          the owner of the product.
        </Trans>
      )
    } else if (currentLegalEntity?.isAPurchaser) {
      return (
        <Trans
          t={t}
          i18nKey="createProductModal.purchaserDescription"
          values={{ currentLegalEntity: currentLegalEntity?.name }}
        >
          Here you can create an entirely new product to add to the All Products area. You might need to do this so that
          the product can be added to a Buy List, Catalog, or used in a Custom Requisition. {{ currentLegalEntity }}{' '}
          will be set as the owner of any product you create here.
        </Trans>
      )
    } else {
      return (
        <Trans
          t={t}
          i18nKey="createProductModal.supplierDescription"
          values={{ currentLegalEntity: currentLegalEntity?.name }}
        >
          Here you can create an entirely new product to add to the All Products area. You might need to do this so that
          the product can be added to one or more Catalogs. Your Organisation {{ currentLegalEntity }} will be set as
          the owner of any product you create here.
        </Trans>
      )
    }
  }

  const loading =
    createProductLoading ||
    createAndAddProductCatalogLoading ||
    createAndAddProductBuyListLoading ||
    createAndAddProductInvoiceLoading ||
    createAndAddProductGRNoteLoading ||
    createAndAddProductRequisitionLoading ||
    createAndAddProductCreditNoteLoading

  return (
    <Modal showModal={showModal} onCloseModal={onCloseModal} loading={loadingStates.loading} error={error}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} className="overflow-hidden">
        <FormProvider {...formMethods}>
          <Modal.Panel
            className="flex max-h-[80vh] w-full flex-col overflow-hidden rounded-md bg-white shadow-xl transition-all md:max-w-[37.5rem]"
            data-testid="create-product-modal"
          >
            <Modal.Title title={title} onCloseModal={onCloseModal} />
            <Modal.Body>
              <section className="mb-3 rounded-md bg-gray-200 px-4 py-3 text-sm">{getDescription()}</section>
              {!!product && (
                <CreateProductPreview
                  selectedCategory={selectedCategory}
                  image={cropImageUrl || product?.image || undefined}
                />
              )}
              <div className="mt-5 space-y-5 text-sm">
                <CreateProductInformation
                  setSelectedCategory={setSelectedCategory}
                  defaultCategory={product?.category}
                  setRemoveDefaultCategory={setRemoveDefaultCategory}
                  removeDefaultCategory={removeDefaultCategory}
                  hideCode={!!product}
                />
                <ProductUnitSize product={product} />
                <ProductSellPack product={product} />
                <CreateProductValues type={type} showModal={showModal} />
                {!product && <CreateProductPreview selectedCategory={selectedCategory} image={cropImageUrl} />}
                <div>
                  <p className="font-bold">{t('general.productImage', 'Product Image')}</p>
                  <p className="text-gray-500">
                    <Trans t={t} i18nKey="createStockItem.productImageDescription">
                      Click the field below to browse your device or drag and drop a product image here. The image must
                      be a PNG, JPG, WEBP, JPEG or GIF and must be at least 800px wide and 800px high. We suggest using
                      product images on a white background.
                    </Trans>
                  </p>
                  <UploadFile
                    selectedFile={selectedFile}
                    setSelectedFile={setSelectedFile}
                    fileTypes={supportedImageTypes}
                  >
                    <UploadIcon className="mx-auto size-6 text-gray-500" />
                    <p className="my-2 font-bold">{t('general.dropImageHere', 'Drop Image Here')}</p>
                    <p className="text-gray-500">
                      {t('createStockItem.imageInfo', 'Upload a PNG, JPG, WEBP, JPEG or GIF')}
                    </p>
                  </UploadFile>
                </div>
                <CropImage key={remount} imageURL={imageURL} aspect={1} setCropImageUrl={setCropImageUrl} />
              </div>
            </Modal.Body>
            <Modal.Footer className="flex flex-col-reverse items-center justify-end gap-2 md:flex-row">
              <Button
                className="h-11 w-full rounded-md bg-gray-200 px-5 text-sm md:w-fit"
                onClick={onCloseModal}
                type="button"
                data-testid="cancel-button"
              >
                {t('general.cancel', 'Cancel')}
              </Button>
              <Button
                className="h-11 w-full rounded-md bg-primary px-5 text-sm text-white md:w-fit"
                type="submit"
                loading={loading}
                data-testid="submit-button"
              >
                {getSubmitButtonText()}
              </Button>
            </Modal.Footer>
          </Modal.Panel>
        </FormProvider>
      </form>
    </Modal>
  )
}
